'use client';

/**
 * The Edit-in-Word freshness affordances, in one place (VALE-288).
 *
 * Six surfaces let a lawyer open a document in Word, and each of them owes the
 * same answer to the same question: *is what I just typed actually here?* Before
 * this file each answered it in its own hand, in the order the surfaces were
 * built, so a lawyer met the reassurance on some documents and silence on
 * others. The markup, the wording and the icon now come from here.
 *
 * Two facts, never merged into one:
 *
 * - `syncedAt` moves only when a save-back actually landed, so it is what the
 *   "Synced from Word N min ago" line reads.
 * - `checkoutOpen` says a Word session is live, which is what gates the refresh
 *   control. Outside one the read seam is a local no-op, so the control could
 *   not change anything.
 *
 * Together they express the state that used to be rendered as nothing at all:
 * opened in Word with nothing back yet. That state is the one a lawyer most
 * needs named, because it is exactly when they are about to send.
 */

import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/utils/date-format';

/**
 * "Synced from Word 2 minutes ago", or the honest absence of it.
 *
 * Green because it is reassurance rather than a warning, and grey while nothing
 * has come back yet, so the eye reads the settled state as the quiet one.
 */
export function WordSyncedLabel({
  syncedAt,
  className
}: {
  /** ISO 8601 of the most recent save-back, or null if none has landed. */
  syncedAt: string | null;
  className?: string;
}) {
  if (syncedAt) {
    return (
      <span
        className={cn('text-success-subtle-foreground font-medium', className)}
      >
        Synced from Word {formatRelativeTime(syncedAt)}
      </span>
    );
  }
  return (
    <span className={cn('text-muted-foreground font-medium', className)}>
      Waiting for your Word changes
    </span>
  );
}

/**
 * The manual escape hatch behind the freshness label: the lawyer came back, read
 * a timestamp older than their last save, and wants to poke it.
 *
 * A quiet text control rather than a button, because it sits inside a meta line
 * and the page's real actions are the buttons beside it. The spinner is the only
 * feedback a successful pull needs; a surface that wants to speak up when Word
 * could not be reached does so from its own handler, since only it knows how
 * loud that should be.
 */
export function WordRefreshButton({
  onRefresh,
  refreshing,
  /**
   * What the control refreshes, for screen readers. Defaults to the document
   * itself; the two template surfaces refresh a rendered preview and say so.
   */
  label = 'Refresh with the latest Word edit',
  className
}: {
  onRefresh: () => void;
  refreshing: boolean;
  label?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={refreshing}
      aria-label={label}
      className={cn(
        'text-muted-foreground hover:text-foreground-secondary disabled:text-foreground-subtle inline-flex items-center gap-1 underline-offset-2 transition-colors hover:underline disabled:cursor-default disabled:no-underline',
        className
      )}
    >
      <RefreshCw className={cn('h-3 w-3', refreshing && 'animate-spin')} />
      {refreshing ? 'Updating…' : 'Refresh now'}
    </button>
  );
}

/**
 * The label and its control as one dot-separated pair, for the header meta lines
 * that already read `Declaration · v3 · …`.
 *
 * Renders nothing at all when there is neither a sync to report nor a session to
 * refresh: an untouched document has nothing to be fresh or stale about, and a
 * line saying so would be noise on every surface that has never seen Word.
 */
export function WordFreshnessSegments({
  syncedAt,
  checkoutOpen,
  onRefresh,
  refreshing,
  refreshLabel,
  /** Rendered before the label, for a host whose meta line already has content. */
  leadingSeparator = false
}: {
  syncedAt: string | null;
  checkoutOpen: boolean;
  onRefresh: () => void;
  refreshing: boolean;
  refreshLabel?: string;
  leadingSeparator?: boolean;
}) {
  if (!syncedAt && !checkoutOpen) return null;

  /*
    Each separator travels with the segment that follows it, in one inline-flex
    box, so a wrap can never strand a bare "·" at the end of a line. That is not
    hypothetical: the enquiry rail is 320px and the label plus the control do not
    fit on one line there.
  */
  return (
    <>
      <span className="inline-flex items-center gap-2">
        {leadingSeparator && <span className="text-foreground-subtle">·</span>}
        <WordSyncedLabel syncedAt={syncedAt} />
      </span>
      {checkoutOpen && (
        <span className="inline-flex items-center gap-2">
          <span className="text-foreground-subtle">·</span>
          <WordRefreshButton
            onRefresh={onRefresh}
            refreshing={refreshing}
            label={refreshLabel}
          />
        </span>
      )}
    </>
  );
}
